"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoadableModel = exports.INITIAL_LOADABLE_MODEL_META_DATA = void 0;
const immer_1 = __importDefault(require("immer"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const Model_1 = require("./Model");
exports.INITIAL_LOADABLE_MODEL_META_DATA = {
    loading: false,
    syncing: false,
    outdated: false,
    done: false,
    error: null
};
class LoadableModel extends Model_1.Model {
    constructor(loader, initialValue) {
        super(initialValue);
        this.loader = loader;
        this.metaSubject = new rxjs_1.BehaviorSubject(exports.INITIAL_LOADABLE_MODEL_META_DATA);
        this.loadSubscription = null;
        this.syncSubscription = null;
        this.syncComparator = null;
        this.meta = exports.INITIAL_LOADABLE_MODEL_META_DATA;
    }
    syncUpdateData(nextValue) {
        this.setValue(nextValue);
    }
    updateMeta(updater) {
        this.meta = (0, immer_1.default)(this.meta, updater);
        this.metaSubject.next(this.meta);
    }
    getMeta() {
        return Object.assign({}, this.meta);
    }
    getMetaAsObservable() {
        return this.metaSubject.asObservable();
    }
    asObservable() {
        return (0, rxjs_1.merge)(super.asObservable(), this.getMetaAsObservable().pipe((0, operators_1.map)(() => this.getValue())));
    }
    destroy() {
        var _a, _b;
        super.destroy();
        this.metaSubject.complete();
        (_a = this.loadSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = this.syncSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
        this.nextData = null;
        this.nextExtraMeta = null;
    }
    load(payload) {
        var _a, _b;
        (_a = this.loadSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = this.syncSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
        this.updateMeta((prevMeta) => {
            prevMeta.loading = true;
            prevMeta.outdated = false;
            prevMeta.syncing = false;
        });
        this.nextData = null;
        this.nextExtraMeta = null;
        return new Promise((resolve, reject) => {
            this.loadSubscription = this.loader(this.getValue(), payload)
                .pipe((0, operators_1.take)(1))
                .subscribe(({ data, meta }) => {
                this.loadSubscription = null;
                try {
                    this.setValue(data);
                    this.updateMeta((prevMeta) => {
                        prevMeta.done = true;
                        prevMeta.loading = false;
                        prevMeta.error = null;
                        if (meta) {
                            prevMeta.extra = meta;
                        }
                    });
                }
                catch (err) {
                    reject(err);
                }
            }, (error) => {
                this.loadSubscription = null;
                try {
                    this.updateMeta((prevMeta) => {
                        prevMeta.done = true;
                        prevMeta.loading = false;
                        prevMeta.error = error;
                    });
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            }, () => {
                resolve();
                this.loadSubscription = null;
            });
        });
    }
    sync(payload) {
        var _a;
        if (this.getMeta().loading) {
            return Promise.resolve();
        }
        (_a = this.syncSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        this.updateMeta((prevMeta) => {
            prevMeta.syncing = true;
            prevMeta.outdated = false;
        });
        this.nextData = null;
        this.nextExtraMeta = null;
        return new Promise((resolve, reject) => {
            this.syncSubscription = this.loader(this.getValue(), payload)
                .pipe((0, operators_1.take)(1))
                .subscribe(({ data, meta }) => {
                var _a, _b;
                try {
                    const canUpdate = (_b = (_a = this.syncComparator) === null || _a === void 0 ? void 0 : _a.call(this, this.getValue(), data)) !== null && _b !== void 0 ? _b : true;
                    if (canUpdate) {
                        this.syncUpdateData(data);
                        this.updateMeta((prevMeta) => {
                            prevMeta.done = true;
                            prevMeta.outdated = false;
                            prevMeta.syncing = false;
                            prevMeta.error = null;
                            if (meta) {
                                prevMeta.extra = meta;
                            }
                        });
                        return;
                    }
                    this.nextData = data;
                    if (meta) {
                        this.nextExtraMeta = meta;
                    }
                    this.updateMeta((prevMeta) => {
                        prevMeta.syncing = false;
                        prevMeta.outdated = true;
                    });
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            }, (error) => {
                this.syncSubscription = null;
                try {
                    this.updateMeta((prevMeta) => {
                        prevMeta.done = true;
                        prevMeta.syncing = false;
                        prevMeta.error = error;
                    });
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            }, () => {
                this.syncSubscription = null;
                resolve();
            });
        });
    }
    withSyncComparator(comparator) {
        this.syncComparator = comparator;
        return this;
    }
    applyFreshData() {
        if (this.nextData) {
            this.syncUpdateData(this.nextData);
        }
        this.updateMeta((prevMeta) => {
            prevMeta.outdated = false;
            if (this.nextExtraMeta) {
                prevMeta.extra = this.nextExtraMeta;
            }
        });
    }
    dropFreshData() {
        this.nextExtraMeta = null;
        this.nextData = null;
        this.updateMeta((prevMeta) => {
            prevMeta.outdated = false;
        });
    }
    terminate() {
        if (this.loadSubscription && this.meta.loading) {
            this.loadSubscription.unsubscribe();
            this.loadSubscription = null;
            this.updateMeta((prevMeta) => {
                prevMeta.loading = false;
            });
        }
        if (this.syncSubscription && this.meta.syncing) {
            this.syncSubscription.unsubscribe();
            this.syncSubscription = null;
            this.updateMeta((prevMeta) => {
                prevMeta.syncing = false;
            });
        }
    }
}
exports.LoadableModel = LoadableModel;
//# sourceMappingURL=LoadableModel.js.map